import { Trophy } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Avatar, Badge } from '@/shared/ui';
import { playerOf, slotLabel, type BracketMatch, type MatchStatus } from '../model/bracketTypes';
import { elapsedMatchMinutes, formatDurationMinutes } from '../model/scoreEntry';
import { MatchStatusMark } from './MatchStatusMark';

/* ------------------------------------------------------------------ *
 * Wiersz wyniku
 * ------------------------------------------------------------------ */

export interface MatchScoreRowProps {
  match: BracketMatch;
  /** Podpis meczu — np. faza rozgrywek, kort, godzina. */
  meta?: ReactNode;
  /**
   * Gdzie postawic podpis i znacznik statusu:
   * - `side` (domyslnie) — znacznik w lewej rynience, podpis w kolumnie po prawej;
   * - `top` — oba w jednej linii NAD zawodnikami, a wynik dostaje cala szerokosc.
   *
   * `top` jest dla list wynikow w waskiej kolumnie (ekran prezentacji): opis fazy
   * stoi wtedy nad para zamiast walczyc o miejsce z nazwiskami.
   */
  metaPlacement?: 'side' | 'top';
  showAvatars?: boolean;
  /**
   * Kolumny setów. Wyłączamy je tam, gdzie wiersz służy tylko do wskazania
   * pary zawodników (pula meczów do rozplanowania) — puste „–” nic nie wnoszą.
   */
  showScore?: boolean;
  className?: string;
}

/**
 * Stala szerokosc kolumny wyniku — trzy komorki setow (3 × 1.5rem) plus odstepy
 * (2 × 0.375rem). Bez tego wiersz z jednym setem, z trzema i z napisem „walkower"
 * mialby rozna szerokosc, a kolumna opisu obok skakalaby z wiersza na wiersz.
 * `min-w`, nie `w`, zeby mecz do pieciu setow mial sie gdzie rozlac.
 */
const SCORE_BLOCK = 'min-w-[5.25rem] shrink-0';

function ScoreCells({
  scores,
  opponentScores,
  highlightLast,
  walkover,
  isWinner,
}: {
  scores: readonly number[] | undefined;
  /** Wyniki setow przeciwnika — pogrubiamy sety wygrane przez te strone. */
  opponentScores: readonly number[] | undefined;
  highlightLast?: boolean;
  /** Mecz oddany walkowerem — zamiast pustych setow pokazujemy „wo." przy zwyciezcy. */
  walkover?: boolean;
  isWinner?: boolean;
}) {
  if (!scores?.length) {
    // Szerokosc rowna blokowi trzech setow (3 × 1.5rem + 2 × 0.375rem),
    // zeby wiersze walkowera nie rozjezdzaly sie z rozegranymi meczami.
    if (walkover) {
      return (
        <span
          className={cn(
            SCORE_BLOCK,
            'text-right',
            isWinner ? 'text-xs font-bold text-fg-strong' : 'text-sm text-fg-subtle',
          )}
        >
          {isWinner ? 'walkower' : '–'}
        </span>
      );
    }

    return (
      <span className={cn(SCORE_BLOCK, 'flex justify-end gap-1.5')}>
        {[0, 1, 2].map((index) => (
          <span key={index} className="w-6 text-center text-sm text-fg-subtle">
            –
          </span>
        ))}
      </span>
    );
  }

  return (
    <span className={cn(SCORE_BLOCK, 'flex justify-end gap-1.5')}>
      {scores.map((score, index) => {
        const isLast = highlightLast && index === scores.length - 1;
        const wonSet = score > (opponentScores?.[index] ?? Number.POSITIVE_INFINITY);
        return (
          <span
            key={index}
            className={cn(
              'w-6 rounded-md text-center text-sm tabular-nums',
              isLast && 'bg-brand-subtle font-bold text-brand-text',
              !isLast && (wonSet ? 'font-bold text-fg-strong' : 'text-fg-muted'),
            )}
          >
            {score}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Wiersz z wynikiem meczu — uzywany na listach „Ostatnie wyniki”
 * i „Nadchodzące mecze”.
 */
export function MatchScoreRow({
  match,
  meta,
  metaPlacement = 'side',
  showAvatars = false,
  showScore = true,
  className,
}: MatchScoreRowProps) {
  const home = playerOf(match.home);
  const away = playerOf(match.away);

  /** Wynik jednej strony — komorki setow obok nazwiska. */
  const scoreFor = (side: 'home' | 'away') => (
    <ScoreCells
      scores={side === 'home' ? match.homeScores : match.awayScores}
      opponentScores={side === 'home' ? match.awayScores : match.homeScores}
      walkover={match.status === 'walkover'}
      isWinner={match.winner === side}
    />
  );

  /* Znacznik zajmuje stala szerokosc, zeby wiersze bez niego (zaplanowane)
     nie rozjezdzaly sie wzgledem rozstrzygnietych. */
  const statusMark = (
    <span className="flex w-3.5 shrink-0 justify-center">
      <MatchStatusMark status={match.status} />
    </span>
  );

  /** Obie strony meczu z kolumnami setow — wspolne dla obu ustawien podpisu. */
  const sides = (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex items-center gap-2">
        {showAvatars && home && <Avatar name={home.name} size="xs" colorful />}
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-[0.8125rem]',
            match.winner === 'home' ? 'font-bold text-fg-strong' : 'text-fg',
          )}
        >
          {slotLabel(match.home)}
        </span>
        {showScore && scoreFor('home')}
      </div>

      <div className="flex items-center gap-2">
        {showAvatars && away && <Avatar name={away.name} size="xs" colorful />}
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-[0.8125rem]',
            match.winner === 'away' ? 'font-bold text-fg-strong' : 'text-fg',
          )}
        >
          {slotLabel(match.away)}
        </span>
        {showScore && scoreFor('away')}
      </div>
    </div>
  );

  if (metaPlacement === 'top') {
    return (
      <div className={cn('flex flex-col gap-1 py-2', className)}>
        {/* Znacznik zakonczenia i faza meczu w jednej linii nad zawodnikami. */}
        <div className="flex items-center gap-2">
          {statusMark}
          {meta && <div className="min-w-0 flex-1 truncate text-xs text-fg-subtle">{meta}</div>}
        </div>
        {sides}
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-3 py-2', className)}>
      {statusMark}
      {sides}

      {/* Stala szerokosc, bo opisy sa roznej dlugosci („Finał" vs „3. kolejka /
          Kort 2") — bez niej kolumna wyniku przesuwalaby sie w kazdym wierszu. */}
      {meta && <div className="w-28 shrink-0 text-right text-xs text-fg-subtle">{meta}</div>}
    </div>
  );
}

export interface UpcomingMatchMetaProps {
  /** Godzina startu, np. „14:30”. `null` gdy termin nie jest ustalony. */
  time: string | null;
  /** Druga linia — kort albo nazwa turnieju, zaleznie od widoku. */
  place?: string | null;
  /** `sm` to 75% stopnia `md` — weziutka kolumna ekranu prezentacji. */
  size?: 'md' | 'sm';
  className?: string;
}

/** Stopnie pisma obu linii; `sm` jest dokladnie o cwierc mniejszy od `md`. */
const UPCOMING_META_SIZES = {
  md: { time: 'text-base', place: 'text-sm' },
  sm: { time: 'text-xs', place: 'text-[0.65625rem]' },
} as const;

/**
 * Podpis meczu, ktory sie jeszcze nie odbyl — do `meta` w {@link MatchScoreRow}.
 *
 * Termin i miejsce sa tu wazniejsze niz przy meczu rozegranym: widz szuka
 * odpowiedzi „o ktorej i gdzie”, a na ekranie w klubie czyta ja z kilku metrow.
 * Dlatego godzina idzie na wyroznionym tle, a nie drobnym szarym tekstem
 * jak reszta podpisow.
 *
 * Obie linie maja to samo wciecie poziome co tlo godziny — inaczej tekst
 * w plakietce stalby o jej padding w lewo wzgledem linii ponizej.
 */
export function UpcomingMatchMeta({ time, place, size = 'md', className }: UpcomingMatchMetaProps) {
  const scale = UPCOMING_META_SIZES[size];

  return (
    <span className={cn('flex flex-col items-end gap-1 text-right', className)}>
      <span
        className={cn(
          'rounded-md bg-brand-subtle px-2 py-0.5 leading-tight font-extrabold text-brand-text tabular-nums',
          scale.time,
        )}
      >
        {time ?? '—'}
      </span>
      {place && (
        <span className={cn('px-2 leading-tight font-bold text-fg-strong', scale.place)}>
          {place}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Karta meczu na zywo
 * ------------------------------------------------------------------ */

export interface LiveMatchCardProps {
  match: BracketMatch;
  /** Nazwa kortu, np. „Kort 1”. */
  courtLabel: string;
  /** Nazwa rundy, np. „1/8 finału”. */
  roundLabel?: string;
  /**
   * Moment rozpoczecia gry (`startedAt`). Podany — karta liczy i pokazuje,
   * jak dlugo mecz juz trwa, i odswieza to sama co pol minuty.
   */
  startedAt?: string | null;
  /**
   * Kolumny setow. Wylaczamy je tam, gdzie wynik i tak nie jest prowadzony
   * na biezaco (ekran prezentacji) — same myslniki nic widzowi nie mowia.
   */
  showScore?: boolean;
  className?: string;
}

/**
 * Zegar odswiezany co pol minuty. Karta musi liczyc czas sama, bo dane
 * z serwera przychodza rzadziej niz zmienia sie minuta — bez tego licznik
 * staltby w miejscu miedzy odswiezeniami.
 */
function useNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [enabled]);

  return now;
}

/**
 * Karta meczu rozgrywanego w tej chwili — panel „MECZE W TRAKCIE”.
 * Ostatnia kolumna wyniku jest wyroznniona jako gem w toku.
 */
export function LiveMatchCard({
  match,
  courtLabel,
  roundLabel,
  startedAt,
  showScore = true,
  className,
}: LiveMatchCardProps) {
  const home = playerOf(match.home);
  const away = playerOf(match.away);

  const now = useNow(Boolean(startedAt));
  const elapsed = elapsedMatchMinutes(startedAt ?? null, now);

  return (
    <div className={cn('flex flex-col gap-2 py-2.5', className)}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-fg-muted">{courtLabel}</span>
        {roundLabel && (
          <>
            <span className="text-xs text-fg-subtle">•</span>
            <span className="text-xs text-fg-subtle">{roundLabel}</span>
          </>
        )}
        {elapsed !== null && (
          <span className="ml-auto text-xs text-fg-muted tabular-nums">
            {formatDurationMinutes(elapsed)}
          </span>
        )}
        <MatchStatusMark status={match.status} className={cn(elapsed === null && 'ml-auto')} />
      </div>

      <div className="flex items-center gap-2">
        {home && <Avatar name={home.name} size="xs" />}
        <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-fg">
          {slotLabel(match.home)}
        </span>
        {showScore && (
          <ScoreCells
            scores={match.homeScores}
            opponentScores={match.awayScores}
            highlightLast
            walkover={match.status === 'walkover'}
            isWinner={match.winner === 'home'}
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        {away && <Avatar name={away.name} size="xs" />}
        <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-fg">
          {slotLabel(match.away)}
        </span>
        {showScore && (
          <ScoreCells
            scores={match.awayScores}
            opponentScores={match.homeScores}
            highlightLast
            walkover={match.status === 'walkover'}
            isWinner={match.winner === 'away'}
          />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Karta zwyciezcy / finalu
 * ------------------------------------------------------------------ */

export interface WinnerCardProps {
  title?: ReactNode;
  /** Zwyciezca. Dopoki final nie jest rozegrany, karta pokazuje sam tytul. */
  winnerName?: ReactNode;
  courtLabel?: ReactNode;
  className?: string;
}

/**
 * Karta finalu z pucharem — prawy skraj drabinki.
 *
 * Celowo zwiezla: to znacznik konca drabinki, a nie kafel informacyjny. Termin
 * finalu stoi w naglowku rozgrywki, wiec tutaj bylby powtorzeniem i tylko
 * rozpychal karte w pionie.
 */
export function WinnerCard({
  title = 'Finał',
  winnerName,
  courtLabel,
  className,
}: WinnerCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1 rounded-xl border border-brand-200 bg-brand-subtle p-3 text-center',
        className,
      )}
    >
      <Trophy aria-hidden="true" className="size-6 text-brand-700" strokeWidth={1.6} />
      <p className="text-[0.6875rem] font-bold tracking-[0.08em] text-fg-muted uppercase">
        {title}
      </p>
      {winnerName && (
        <p className="text-sm font-extrabold tracking-[-0.02em] text-fg-strong">{winnerName}</p>
      )}
      {courtLabel && <p className="text-xs text-fg-muted">{courtLabel}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Podium turnieju
 * ------------------------------------------------------------------ */

/** Wpis podium — miejsce 1-3 i etykieta zawodnika/pary. */
export interface PodiumCardEntry {
  place: number;
  label: string;
}

export interface PodiumCardProps {
  title?: ReactNode;
  /** Miejsca rosnąco; brakujące (np. nierozstrzygnięte 3. miejsce) po prostu pomijamy. */
  entries: readonly PodiumCardEntry[];
  className?: string;
}

/** Ton plakietki miejsca: złoto marki, neutralne srebro, „brązowe" ostrzeżenie. */
const PODIUM_TONES = ['brand', 'neutral', 'warning'] as const;

/**
 * Podium turnieju — miejsca 1-3 po rozstrzygnięciu rozgrywki. Wspólne dla
 * wszystkich systemów: drabinki, grupy finałowej, grup klasyfikacyjnych i ligi.
 */
export function PodiumCard({ title = 'Podium', entries, className }: PodiumCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-brand-200 bg-brand-subtle p-4',
        className,
      )}
    >
      <p className="flex items-center gap-2 text-[0.6875rem] font-bold tracking-[0.08em] text-fg-muted uppercase">
        <Trophy aria-hidden="true" className="size-4 text-brand-700" strokeWidth={1.6} />
        {title}
      </p>
      <ol className="flex flex-col gap-2">
        {entries.map((entry) => (
          <li key={entry.place} className="flex min-w-0 items-center gap-2.5">
            <Badge
              tone={PODIUM_TONES[entry.place - 1] ?? 'neutral'}
              size="sm"
              className="w-6 shrink-0 justify-center tabular-nums"
            >
              {entry.place}
            </Badge>
            <Avatar name={entry.label} size="xs" colorful />
            <span
              className={cn(
                'truncate text-sm',
                entry.place === 1 ? 'font-extrabold text-fg-strong' : 'font-semibold text-fg',
              )}
            >
              {entry.label}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Ranking zawodnikow
 * ------------------------------------------------------------------ */

export interface RankedPlayer {
  id: string;
  name: string;
  /** Opis pod nazwiskiem, np. „Półfinalista”. */
  note?: string;
  seed?: number;
  avatarUrl?: string;
}

export interface PlayerRankListProps {
  players: readonly RankedPlayer[];
  className?: string;
}

/** Lista najlepszych zawodnikow — panel „NAJLEPSI ZAWODNICY”. */
export function PlayerRankList({ players, className }: PlayerRankListProps) {
  return (
    <ol className={cn('flex flex-col', className)}>
      {players.map((player, index) => (
        <li
          key={player.id}
          className="flex items-center gap-3 border-b border-border py-2.5 last:border-b-0"
        >
          <span className="w-4 shrink-0 text-sm font-bold text-fg-subtle tabular-nums">
            {index + 1}
          </span>
          <Avatar
            name={player.name}
            size="sm"
            {...(player.avatarUrl ? { src: player.avatarUrl } : {})}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[0.8125rem] font-semibold text-fg-strong">
              {player.name}
              {player.seed !== undefined && (
                <span className="font-normal text-fg-muted"> (Nr {player.seed})</span>
              )}
            </p>
            {player.note && <p className="truncate text-xs text-fg-subtle">{player.note}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ------------------------------------------------------------------ *
 * Etykieta statusu meczu
 * ------------------------------------------------------------------ */

const statusTone: Record<MatchStatus, 'success' | 'brand' | 'info' | 'warning' | 'neutral'> = {
  finished: 'success',
  live: 'brand',
  suspended: 'warning',
  scheduled: 'info',
  walkover: 'warning',
  retired: 'neutral',
};

export function MatchStatusBadge({ status, label }: { status: MatchStatus; label: string }) {
  return (
    <Badge tone={statusTone[status]} size="sm">
      <MatchStatusMark status={status} size="sm" />
      {label}
    </Badge>
  );
}
